<script setup lang="ts">
import {computed, ref, watch} from 'vue';
import {useDebounceFn} from '@vueuse/core';
import {WorldCountryCode} from '@/config/world-countries-en';
import { Languages, PlaceTypes, type Prediction} from '@/core/google/type';
import useGoogleMaps from '@/composables/useGoogleMaps';
import StfAutoComplete from '@/ui/autocomplete/StfAutoComplete.vue';
import {storeToRefs} from 'pinia';
import {useOwnerStore} from '@/store/owner';
import addressMappingService from '@/core/google/address-mapping';
import {useApplicationStore} from '@/store/application';

const ownerStore = useOwnerStore();
const { hasValidation } = storeToRefs(useApplicationStore());
const { address, addressError, addressIsValid } = storeToRefs(ownerStore);
const { country } = storeToRefs(ownerStore);
const loading = ref(false);
const inputAddress = ref(address.value);
const searchAddress = ref('');
const addressResult = ref<Prediction[]>([])
const {googleApi} = useGoogleMaps();

const countriesOptions = computed(() => {
    if (loading.value) {
        return [{ type: 'loading' }];
    }

    const options = [];
    if (searchAddress.value.length > 0) {
        options.push({
            label: searchAddress.value,
            value: searchAddress.value
        });
        options.push({ type: 'separator' });
    }

    addressResult.value.forEach((item) => {
        options.push({
            label: item.description,
            value: item.place_id
        });
    });

    return options as any;
})

const loadPlaces = useDebounceFn(async () => {
    addressResult.value = await googleApi.value.autocomplete(inputAddress.value, {
        language: Languages.EN,
        types: PlaceTypes.ADDRESS,
        country: country.value as WorldCountryCode,
        region: WorldCountryCode.AU
    });

    loading.value = false;
}, 700);

const onSelect = (value: string) => {
    const result = addressResult.value.find((item) => item.place_id === value);
    if (!result) {
        address.value = value;
        return;
    }

    const mappedAddress = addressMappingService.mapAddressFor(result, country.value as WorldCountryCode);

    ownerStore.setAddress(mappedAddress);
    if (mappedAddress.getAddress().length === 0) {
        address.value = inputAddress.value;
    } else {
        inputAddress.value = mappedAddress.getAddress();
    }
}

watch(inputAddress, (value) => {
    if (value.length === 0) {
        address.value = '';
        return;
    }

    if (value === address.value) {
        return;
    }

    searchAddress.value = value;
    loading.value = true;
    loadPlaces();
})

watch(address, (newValue) => {
    inputAddress.value = newValue;
})
</script>

<template>
    <StfAutoComplete
        v-model="inputAddress"
        label="Address"
        placeholder="Street address or PO Box"
        :loading="loading"
        required
        :options="countriesOptions"
        :error="hasValidation ? addressError : null"
        :success="addressIsValid"
        @update:selected="onSelect"
    ></StfAutoComplete>
</template>

<style scoped>

</style>
